import { useState } from "react";
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { Card, Table, Badge, Button, Form, Row, Col } from "react-bootstrap";
import { callsAPI } from "../services/api";

const LIMIT_OPTIONS = [25, 50, 100, 500];

const getTimeLeftLabel = (value) => {
  if (!value) return "N/A";

  const scheduledTime = new Date(value).getTime();
  if (Number.isNaN(scheduledTime)) return "N/A";

  const diffMs = scheduledTime - Date.now();
  if (diffMs <= 0) return "Expired";

  const totalMinutes = Math.floor(diffMs / 60000);
  if (totalMinutes < 60) return `${totalMinutes}m left`;

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours < 24) {
    return minutes > 0 ? `${hours}h ${minutes}m left` : `${hours}h left`;
  }

  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  if (remainingHours > 0) {
    return `${days}d ${remainingHours}h left`;
  }
  return `${days}d left`;
};

// Centralized here instead of recomputed inline in three places (mobile
// card, desktop row, and previously a duplicated ternary) — one source of
// truth for what color a given "time left" label gets.
const getTimeLeftVariant = (label) => {
  if (label === "Expired") return "danger";
  if (label.includes("m left") || label.includes("h left")) return "warning";
  if (label === "N/A") return "secondary";
  return "success";
};

// Separate from the main dashboard entirely: its own state, its own query,
// its own refresh action. Reads are always fast (plain DB read); the
// "Refresh" button is the only thing that ever talks to ElevenLabs.
const ScheduledCallsTab = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState(null);

  const { data, isLoading, isError, isFetching, isPlaceholderData } = useQuery({
    queryKey: ["scheduledCalls", page, limit],
    queryFn: async () => {
      const res = await callsAPI.getScheduledCalls({ page, limit, page_size: limit });
      return res.data;
    },
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

  const results = data?.results || [];
  const totalPages = data?.total_pages || 1;
  const totalRecords = data?.total_records ?? results.length;
  const lastRefreshedAt = data?.last_refreshed_at;

  const handleRefresh = async () => {
    setIsRefreshing(true);
    setRefreshError(null);
    try {
      await callsAPI.refreshScheduledCalls();
      await queryClient.invalidateQueries({ queryKey: ["scheduledCalls"] });
    } catch (err) {
      setRefreshError("Refresh failed — check that ELEVENLABS_API_KEY is set on the backend.");
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <Card className="border-0 shadow-sm">
      <Card.Body className="p-2 p-sm-3">
        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start gap-2 mb-3">
          <div>
            <div className="fw-semibold small">Scheduled Calls</div>
            <div className="small text-muted">
              {totalRecords} pending batch{totalRecords === 1 ? "" : "es"} from ElevenLabs
              {lastRefreshedAt && <> · last refreshed {lastRefreshedAt}</>}
              <span className="ms-2">• Showing {limit} per page</span>
            </div>
          </div>

          <Button
            variant="outline-primary"
            size="sm"
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="flex-shrink-0"
          >
            <span className="d-none d-sm-inline">
              {isRefreshing ? "Refreshing from ElevenLabs…" : "Refresh from ElevenLabs"}
            </span>
            <span className="d-inline d-sm-none" aria-hidden="true">
              {isRefreshing ? "…" : "⟳ Refresh"}
            </span>
          </Button>
        </div>

        {refreshError && <div className="text-danger small mb-3">{refreshError}</div>}

        <Row className="g-2 align-items-center mb-3">
          <Col xs="auto">
            <Form.Label className="small text-muted mb-0 me-2">Rows per page</Form.Label>
          </Col>
          <Col xs="auto">
            <Form.Select
              size="sm"
              value={limit}
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setPage(1);
              }}
              style={{ width: 95 }}
            >
              {LIMIT_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Form.Select>
          </Col>
        </Row>

        {isLoading ? (
          <div className="text-center text-muted py-5 small">Loading scheduled calls…</div>
        ) : isError ? (
          <div className="text-center text-danger py-5 small">Failed to load scheduled calls.</div>
        ) : results.length === 0 ? (
          <div className="text-center text-muted py-5 small">
            No scheduled calls right now. Try "Refresh from ElevenLabs" to pull the latest.
          </div>
        ) : (
          <>
            {/* Mobile: stacked cards, no horizontal scrolling required. */}
            <div className="d-md-none">
              {results.map((call) => {
                const timeLeftLabel = getTimeLeftLabel(call.scheduled_time);
                const timeLeftVariant = getTimeLeftVariant(timeLeftLabel);

                return (
                  <Card key={call.batch_id} className="border-0 shadow-sm mb-2">
                    <Card.Body className="p-2 px-3">
                      <div className="d-flex justify-content-between align-items-start gap-2">
                        <div className="flex-grow-1 min-width-0">
                          <div className="fw-semibold text-truncate">{call.name || "N/A"}</div>
                          <div className="small text-muted">{call.phone || "N/A"}</div>
                        </div>
                        <Badge bg={timeLeftVariant} className="flex-shrink-0">
                          {timeLeftLabel}
                        </Badge>
                      </div>

                                <div className="small text-muted mt-1">
                        <span className="fw-semibold text-dark">Scheduled at:</span>{" "}
                        {call.scheduled_at || call.scheduled_time || "N/A"}
                      </div>

                      <div className="small text-muted mt-1 text-truncate">
                        {call.address || "No address"}
                      </div>

                      <div className="d-flex justify-content-between align-items-center mt-2 gap-2">
                        <div>
                          {call.service ? (
                            <Badge bg="info">{call.service}</Badge>
                          ) : (
                            <span className="small text-muted">No service listed</span>
                          )}
                        </div>
                        <div className="small text-muted text-truncate text-end" style={{ maxWidth: "55%" }}>
                          {call.batch_name || call.batch_id}
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                );
              })}
            </div>

            {/* Desktop: table, unchanged. */}
            <div className="d-none d-md-block table-responsive">
              <Table hover size="sm" className="mb-0 align-middle small">
                <thead className="table-light">
                  <tr>
                    <th>Scheduled At</th>
                    <th>Time Left</th>
                    <th>Contact</th>
                    <th>Address</th>
                    <th>Service</th>
                    <th>Batch</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((call) => {
                    const timeLeftLabel = getTimeLeftLabel(call.scheduled_time);
                    const timeLeftVariant = getTimeLeftVariant(timeLeftLabel);

                    return (
                      <tr key={call.batch_id}>
                        <td className="text-nowrap">
                          {call.scheduled_at || call.scheduled_time || "N/A"}
                        </td>
                        <td className="text-nowrap">
                          <span className={`fw-semibold text-${timeLeftVariant}`}>
                            {timeLeftLabel}
                          </span>
                        </td>
                        <td style={{ minWidth: 160 }}>
                          <div className="fw-semibold">{call.name || "N/A"}</div>
                          <div className="text-muted">{call.phone || "N/A"}</div>
                        </td>
                        <td style={{ minWidth: 180, maxWidth: 240 }} className="text-truncate">
                          {call.address || "N/A"}
                        </td>
                        <td>
                          {call.service ? <Badge bg="info">{call.service}</Badge> : "N/A"}
                        </td>
                        <td className="text-muted">{call.batch_name || call.batch_id}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          </>
        )}

        <div className="d-flex flex-row justify-content-between align-items-center gap-2 mt-3">
          <Button
            variant="outline-secondary"
            size="sm"
            disabled={page <= 1 || (isFetching && isPlaceholderData)}
            onClick={() => {
              setPage((current) => Math.max(1, current - 1));
              queryClient.invalidateQueries({ queryKey: ["scheduledCalls"] });
            }}
          >
            ← Previous {limit}
          </Button>

          <span className="small text-muted text-center">
            Page {page} of {totalPages}
            <span className="d-none d-sm-inline"> • {limit} records/page</span>
            {isFetching && isPlaceholderData && (
              <span className="ms-2">Updating…</span>
            )}
          </span>

          <Button
            variant="outline-secondary"
            size="sm"
            disabled={page >= totalPages || (isFetching && isPlaceholderData)}
            onClick={() => {
              setPage((current) => Math.min(totalPages, current + 1));
              queryClient.invalidateQueries({ queryKey: ["scheduledCalls"] });
            }}
          >
            Next {limit} →
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
};

export default ScheduledCallsTab;